import { jsx } from 'slate-hyperscript';
import type { Descendant, Editor, Element, Text } from 'slate';
import type { HTMLElements, HTMLLeafProps } from './types';

export const ELEMENT_TAGS: Record<string, (el: HTMLElement) => Omit<HTMLElements, 'children'>> = {
  A: (el) => ({ type: 'link', url: el.getAttribute('href')! }),
  BLOCKQUOTE: () => ({ type: 'block-quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  IMG: (el) => ({ type: 'image', url: el.getAttribute('src')! }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  PRE: () => ({ type: 'code-block' }),
  UL: () => ({ type: 'bulleted-list' }),
};

export const TEXT_TAGS: Record<string, () => HTMLLeafProps> = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

const innerReg = /inline/;

export function isInlineElement(el: globalThis.Element) {
  const { display } = getComputedStyle(el);
  return innerReg.test(display);
}

export function deserialize(el: HTMLElement | ChildNode) {
  const root: (Descendant | undefined)[] = [];

  function core(el: ChildNode): Descendant | undefined {
    const { nodeName, childNodes, nodeType, textContent } = el;
    if (nodeType === Node.TEXT_NODE) {
      return textContent ? jsx('text', textContent) : undefined;
    } else if (nodeName === 'BR') {
      return jsx('element');
    } else if (nodeType === Node.ELEMENT_NODE) {
      const isInline = isInlineElement(el as any);
      return isInline
        ? jsx('text', {}, (el as globalThis.HTMLElement).innerText)
        : jsx('element', {}, Array.from(childNodes).map(core));
    }
  }

  for (const node of el.childNodes) {
    root.push(core(node));
  }

  return jsx('fragment', {}, root);
}

export function isHTMLEls(element: Element) {
  const types: Array<HTMLElements['type']> = [
    'paragraph',
    'block-quote',
    'heading-one',
    'heading-two',
    'heading-three',
    'heading-four',
    'heading-five',
    'heading-six',
    'image',
    'list-item',
    'numbered-list',
    'code-block',
    'bulleted-list',
  ];

  return types.includes(element.type as any);
}

export function isHTMLLeaf(leaf: Text) {
  const props: Array<keyof HTMLLeafProps> = [
    'code',
    'strikethrough',
    'italic',
    'bold',
    'underline',
  ];
  return props.some((p) => p in leaf);
}

export function withHTMLEditor(editor: Editor) {
  const { insertData, isVoid } = editor;

  editor.isVoid = (element: Element) => {
    return element.type === 'image' || isVoid(element);
  };

  editor.insertData = (data) => {
    /*
    const html = data.getData('text/html');

    if (html) {
      const container = document.createElement('div');
      container.style.display = 'none';
      container.innerHTML = html;

      const childNodes = Array.from(container.childNodes);

      for (const node of childNodes) {
        switch (node.nodeType) {
          case Node.TEXT_NODE:
            if (!node.nodeValue?.trim()) {
              node.remove();
            }
            break;
          case Node.ELEMENT_NODE:
            break;
          default:
            node.remove();
            break;
        }
      }

      document.body.appendChild(container);

      const fragment = deserialize(container);
      editor.insertFragment(fragment);

      container.remove();
      return;
    }
    */
    insertData(data);
  };

  return editor;
}
